// Inventory adjust modal — POST /api/inventory/adjust.
//
// Reason is required (backend enforces). The XOR location shape is handled
// server-side; the client always sends location_id + signed quantity_delta.
// Capability: operations | admin (anyone without it will hit a 403 toast).
//
// Financial users additionally get a "Unit cost" section that overrides the
// SKU's weighted-avg unit_cost via PATCH /api/inventory/price. It is an
// INDEPENDENT action (its own Save button) — no quantity change required —
// and every override is recorded server-side in parts_price_history, surfaced
// here as a short history list. Note: the next receive/consume of this SKU
// recomputes the weighted-avg and overwrites a manual override.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Radio, RadioGroup } from '../components/ui/Radio';
import { Select } from '../components/ui/Select';
import { Tooltip } from '../components/ui/Tooltip';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import Dialog from '../components/ui/Dialog';
import Reveal from '../components/ui/Reveal';
import { itemLabel, type ItemNameSource } from '../components/ItemName';
import type { LocationRow } from '../Receiving/types';

interface PriceHistoryRow {
  id: number;
  old_unit_cost: string | null;
  new_unit_cost: string | null;
  reason: string | null;
  changed_at: string;
  changed_by_name: string | null;
}

interface Props {
  partCatalogId: number;
  sku: string;
  /**
   * The item, so the drawer's title can name it the way the page did.
   *
   * OPTIONAL, and `sku` stays required: a caller that only has the code (the
   * box detail page reaches this from a barcode) still gets a correct title,
   * and nothing had to change to keep working.
   */
  item?: ItemNameSource;
  // Pre-seed when called from a box detail page; user can still override.
  locationId?: number | null;
  locationName?: string | null;
  // Current weighted-avg unit cost (dollars, NUMERIC serialized as string).
  // Prefills the financial price field. Undefined when the caller doesn't know it.
  unitCost?: string | number | null;
  onClose: () => void;
  onAdjusted: (result: { transaction_id: number; new_quantity: number | null }) => void;
  // Called after a successful price override so the caller can refresh its view.
  onPriceChanged?: (newUnitCost: number | null) => void;
}

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

const fmtCost = (n: string | number | null | undefined) =>
  n === null || n === undefined || n === '' ? '—' : `$${parseFloat(String(n)).toFixed(2)}`;

const InventoryAdjustAction: React.FC<Props> = ({
  partCatalogId,
  sku,
  item,
  locationId,
  locationName,
  unitCost,
  onClose,
  onAdjusted,
  onPriceChanged,
}) => {
  const api = useApi();
  const toast = useToast();
  // Strict: financial visibility is opt-in even for admins (see require-financial).
  const canSeeFinancials = useUserStore((s) => !!s.user.can_financial);
  // Quantity adjustment is operations/admin (matches the backend /adjust gate).
  // A financial-only user opens this drawer purely to edit the unit cost.
  const canAdjustQty = useUserStore(
    (s) => !!(s.user.isadmin || s.user.can_admin || s.user.can_operations)
  );
  const [direction, setDirection] = useState<'add' | 'remove'>('remove');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [locId, setLocId] = useState<string>(locationId ? String(locationId) : '');
  // The location the dialog opened with; the warehouse default that arrives after the fetch counts as opened-with.
  const openedLocId = useRef(locId);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Financial-only price override state.
  const [priceInput, setPriceInput] = useState<string>(
    unitCost === null || unitCost === undefined || unitCost === '' ? '' : String(parseFloat(String(unitCost)))
  );
  const [priceReason, setPriceReason] = useState('');
  const [priceSubmitting, setPriceSubmitting] = useState(false);
  const [currentCost, setCurrentCost] = useState<string | number | null | undefined>(unitCost);
  const [history, setHistory] = useState<PriceHistoryRow[]>([]);

  useEffect(() => {
    (async () => {
      try {
        // Warehouses and the rack sections beneath them: stock sits on a section once it is put away.
        const all = (await api.get('/locations')) as LocationRow[];
        const rows = (all || []).filter((l) => l.type === 'warehouse' || l.type === 'section');
        // Areas stay in state only so a section can find its warehouse; they are never listed.
        setLocations([...rows, ...(all || []).filter((l) => l.type === 'area')]);
        if (!locationId && rows.length > 0) {
          const first = rows.find((l) => l.type === 'warehouse' && l.is_active);
          if (first) {
            openedLocId.current = String(first.id);
            setLocId(String(first.id));
          }
        }
      } catch {
        // Silent — adjust still works if locations don't load, since the
        // initial location_id is usually pre-seeded by the box.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One group per warehouse: the warehouse first, then its sections (the area between is not a place).
  const groups = useMemo(() => {
    const byId = new Map(locations.map((l) => [l.id, l]));
    const sectionsOf = new Map<number, LocationRow[]>();
    const loose: LocationRow[] = [];
    for (const l of locations) {
      if (l.type !== 'section') continue;
      const area = l.parent_location_id != null ? byId.get(l.parent_location_id) : undefined;
      const wh = area?.parent_location_id ?? l.parent_location_id;
      if (wh != null && byId.get(wh)?.type === 'warehouse') sectionsOf.set(wh, [...(sectionsOf.get(wh) ?? []), l]);
      else loose.push(l);
    }
    const sorted = (xs: LocationRow[]) => [...xs].sort((a, b) => (a.code || a.name).localeCompare(b.code || b.name, undefined, { numeric: true }));
    return [
      ...locations
        .filter((l) => l.type === 'warehouse')
        .map((w) => ({ key: String(w.id), label: w.name, places: [w, ...sorted(sectionsOf.get(w.id) ?? [])] })),
      ...(loose.length ? [{ key: 'loose', label: 'Other sections', places: sorted(loose) }] : []),
    ];
  }, [locations]);
  // A pre-seeded place that is not in the list (retired, or the list failed) stays selected, never swapped.
  const seededMissing = !!locId && !groups.some((g) => g.places.some((l) => String(l.id) === locId));

  const openedPrice = currentCost === null || currentCost === undefined || currentCost === '' ? '' : String(parseFloat(String(currentCost)));
  const dirty =
    direction !== 'remove' ||
    quantity !== '' ||
    reason !== '' ||
    locId !== openedLocId.current ||
    // Read as the save reads it: "12.50" saved comes back as 12.5 and is not typed work.
    (priceInput.trim() === '' ? '' : String(Number(priceInput.trim()))) !== openedPrice ||
    priceReason !== '';

  const loadHistory = async () => {
    try {
      const rows = (await api.get(
        `/inventory/price-history?part_catalog_id=${partCatalogId}&limit=5`
      )) as PriceHistoryRow[];
      setHistory(rows || []);
    } catch {
      // Non-fatal — the override still works without the history list.
    }
  };

  useEffect(() => {
    if (canSeeFinancials) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeFinancials, partCatalogId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const q = parseInt(quantity, 10);
    if (!Number.isInteger(q) || q <= 0) {
      toast.error('Quantity must be a positive whole number');
      return;
    }
    if (!reason.trim()) {
      toast.error('Reason is required for adjustments');
      return;
    }
    if (!locId) {
      toast.error('Pick a location');
      return;
    }
    const signed = direction === 'add' ? q : -q;
    setSubmitting(true);
    try {
      const result = (await api.post('/inventory/adjust', {
        body: {
          part_catalog_id: partCatalogId,
          location_id: parseInt(locId, 10),
          quantity_delta: signed,
          reason: reason.trim(),
        },
      })) as { ok: true; transaction_id: number; new_quantity: number | null };
      toast.success(
        `Adjusted ${direction === 'add' ? '+' : '−'}${q} ${sku} (now ${result.new_quantity ?? '?'})`
      );
      onAdjusted(result);
    } catch (err: any) {
      toast.error(err?.message || 'Adjust failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePrice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (priceSubmitting) return;
    const trimmed = priceInput.trim();
    let parsed: number | null = null;
    if (trimmed !== '') {
      parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error('Unit cost must be a non-negative number (or blank to clear)');
        return;
      }
    }
    setPriceSubmitting(true);
    try {
      const result = (await api.patch('/inventory/price', {
        body: {
          part_catalog_id: partCatalogId,
          unit_cost: trimmed === '' ? null : parsed,
          reason: priceReason.trim() || null,
        },
      })) as { ok: true; unit_cost: number | null };
      setCurrentCost(result.unit_cost);
      setPriceReason('');
      toast.success(`Unit cost for ${sku} set to ${fmtCost(result.unit_cost)}`);
      onPriceChanged?.(result.unit_cost);
      loadHistory();
    } catch (err: any) {
      toast.error(err?.message || 'Price update failed');
    } finally {
      setPriceSubmitting(false);
    }
  };

  return (
    <Dialog open title={`Adjust: ${item ? itemLabel(item) : sku}`} onClose={onClose} size="lg" busy={submitting || priceSubmitting} dirty={dirty}>
      {canAdjustQty && (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <RadioGroup
            label="Direction"
            name="adjust-direction"
            layout="row"
            value={direction}
            onChange={(v) => setDirection(v as 'add' | 'remove')}
          >
            <Radio value="remove" label="Remove" />
            <Radio value="add" label="Add" />
          </RadioGroup>
          <label className="block">
            <span className={labelClass}>Quantity</span>
            <Field
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="5"
              autoFocus
            />
          </label>
        </div>

        <label className="block">
          <span className={labelClass}>Location</span>
          <Select
            label="Location"
            value={locId}
            onChange={setLocId}
            placeholder="Select…"
            options={[
              { key: '', label: 'Select…' },
              ...(seededMissing ? [{ key: locId, label: locationName || 'Current location' }] : []),
              ...groups.flatMap((g) =>
                g.places.map((l) => ({
                  key: String(l.id),
                  label: l.type === 'section' ? l.code || l.name : l.name,
                  group: g.label,
                })),
              ),
            ]}
          />
          <Reveal show={!!locationName && !locId}>
            <p className="text-xs text-on-surface-variant mt-1">Box was at: {locationName}</p>
          </Reveal>
        </label>

        <label className="block">
          <span className={labelClass}>Reason</span>
          <Field
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder='e.g. "QC reject — 3 boards failed program"'
          />
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={buttonClass('filled')}
          >
            {submitting ? 'Adjusting…' : 'Confirm adjustment'}
          </button>
        </div>
      </form>
      )}

      {canSeeFinancials && (
        <div className={canAdjustQty ? 'mt-5 pt-5 border-t border-gray-700/60' : ''}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-amber-300">Unit cost</h3>
            <span className="text-xs text-on-surface-variant">Financial</span>
          </div>
          <p className="text-xs text-on-surface-variant mb-3">
            Current: <span className="text-on-surface tabular-nums">{fmtCost(currentCost)}</span>.
            Manual override — the next receive or consume of this SKU recomputes the weighted average.
          </p>
          <form onSubmit={handleSavePrice} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelClass}>New unit cost ($)</span>
                <Field
                  type="number"
                  min={0}
                  step="0.01"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="blank = clear"
                />
              </label>
              <label className="block">
                <span className={labelClass}>Note (optional)</span>
                <Field
                  value={priceReason}
                  onChange={(e) => setPriceReason(e.target.value)}
                  placeholder='e.g. "vendor quote 2026-06"'
                />
              </label>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={priceSubmitting}
                className={buttonClass('quiet')}
              >
                {priceSubmitting ? 'Saving…' : 'Save price'}
              </button>
            </div>
          </form>

          <Reveal show={history.length > 0}>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-on-surface-variant mb-1">
                Recent overrides
              </div>
              <ul className="space-y-1">
                {history.map((h) => (
                  <li key={h.id} className="text-xs text-on-surface-variant flex justify-between gap-2">
                    <span className="tabular-nums">
                      {fmtCost(h.old_unit_cost)} → <span className="text-on-surface">{fmtCost(h.new_unit_cost)}</span>
                    </span>
                    <Tooltip label={h.reason || ''} truncated>
                      <span className="truncate text-on-surface-variant">
                        {h.changed_by_name || 'Unknown'}
                        {h.reason ? ` · ${h.reason}` : ''}
                      </span>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      )}
    </Dialog>
  );
};

export default InventoryAdjustAction;
